import {
  ChainType,
  getToken,
  getTokens,
  type TokenExtended,
  type TokensExtendedResponse,
} from '@lifi/sdk'
import { useChainTypeFromAddress } from '@lifi/widget-provider'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSDKClient } from '../providers/SDKClientProvider.js'
import { useWidgetConfig } from '../providers/WidgetProvider/WidgetProvider.js'
import type { FormType } from '../stores/form/types.js'
import type { TokensByChain } from '../types/token.js'
import { defaultChainIdsByType } from '../utils/chainType.js'
import { isItemAllowed } from '../utils/item.js'
import { getQueryKey } from '../utils/queries.js'
import {
  filterAllowedTokens,
  getNativeTokenAddresses,
  mergeVerifiedWithSearchTokens,
} from '../utils/token.js'
import { useAvailableChains } from './useAvailableChains.js'

const refetchInterval = 300_000

export const useTokens = (
  formType?: FormType,
  search?: string,
  chainId?: number
): {
  allTokens: Record<number, TokenExtended[]> | undefined
  isLoading: boolean
  isSearchLoading: boolean
} => {
  const {
    tokens: configTokens,
    chains: chainsConfig,
    keyPrefix,
  } = useWidgetConfig()
  const sdkClient = useSDKClient()
  const { getChainTypeFromAddress } = useChainTypeFromAddress()

  // Main tokens cache - verified tokens from API
  const { data: verifiedTokens, isLoading } = useQuery({
    queryKey: [getQueryKey('tokens', keyPrefix)],
    queryFn: async ({ signal }) => {
      const chainTypes = [
        ChainType.EVM,
        ChainType.SVM,
        ChainType.UTXO,
        ChainType.MVM,
        ChainType.TVM,
        ChainType.STL,
      ].filter((chainType) => isItemAllowed(chainType, chainsConfig?.types))

      const tokensResponse: TokensExtendedResponse = await getTokens(
        sdkClient,
        {
          chainTypes,
          orderBy: 'volumeUSD24H',
          extended: true,
          limit: 1000,
          minPriceUSD: 0.000001,
        },
        { signal }
      )

      // `listed` tells a main-list token from a searched one
      const tokens: TokensByChain = Object.fromEntries(
        Object.entries(tokensResponse.tokens).map(([chainId, tokens]) => [
          chainId,
          tokens.map((token) => ({ ...token, listed: true })),
        ])
      )

      return tokens
    },
    refetchInterval,
    staleTime: refetchInterval,
  })

  // Search tokens cache - unverified tokens from search
  const { data: searchTokens, isLoading: isSearchLoading } = useQuery({
    queryKey: [getQueryKey('tokens-search', keyPrefix), search, chainId],
    queryFn: async ({ queryKey, signal }) => {
      const [, searchQuery, searchChainId] = queryKey as [
        string,
        string,
        number,
      ]
      const chainTypes = [
        ChainType.EVM,
        ChainType.SVM,
        ChainType.UTXO,
        ChainType.MVM,
        ChainType.TVM,
        ChainType.STL,
      ].filter((chainType) => isItemAllowed(chainType, chainsConfig?.types))

      const tokensResponse: TokensExtendedResponse = await getTokens(
        sdkClient,
        {
          chainTypes,
          orderBy: 'volumeUSD24H',
          extended: true,
          search: searchQuery,
          limit: 1000,
          minPriceUSD: 0.000001,
        },
        { signal }
      )

      // If the chainId is not provided, try to get it from the search query
      let _chainId = searchChainId
      if (!_chainId) {
        const chainType = getChainTypeFromAddress(searchQuery)
        if (chainType && chainType in defaultChainIdsByType) {
          _chainId = defaultChainIdsByType[chainType]
        }
      }

      // Fallback: If the main search returned no tokens for the specific chainId,
      // fetch a single token using the /token endpoint
      if (_chainId && searchQuery) {
        const existingTokens = tokensResponse.tokens[_chainId] || []
        if (!existingTokens.length) {
          const token = await getToken(sdkClient, _chainId, searchQuery, {
            signal,
          })
          if (token) {
            tokensResponse.tokens[_chainId] = [token]
          }
        }
      }

      return tokensResponse.tokens as TokensByChain
    },
    enabled: !!search,
    refetchInterval,
    staleTime: refetchInterval,
  })

  const { chains } = useAvailableChains()
  const nativeTokenAddresses = useMemo(
    () => getNativeTokenAddresses(chains),
    [chains]
  )

  // Merge tokens at read time - single place where caches are combined
  const allTokens = useMemo(() => {
    const merged = mergeVerifiedWithSearchTokens(verifiedTokens, searchTokens)
    return filterAllowedTokens(
      merged,
      configTokens,
      chainsConfig,
      formType,
      nativeTokenAddresses
    )
  }, [
    verifiedTokens,
    searchTokens,
    configTokens,
    chainsConfig,
    formType,
    nativeTokenAddresses,
  ])

  return {
    allTokens,
    isLoading,
    isSearchLoading,
  }
}
